import { GeoLocation } from '@dcxwam/stores-sdk'
import { useCallback, useEffect, useState } from 'react'

/**
 *
 * @param positionOptions
 * @param triggerOnAction If false means no user event required
 *            If true means user interaction is required, when using trigger use true by default
 *            Update this to false when location is to requested
 * @returns location, locationError, and fetchLocation function for on-demand fetching
 */
export const useUserLocation = (
  positionOptions?: PositionOptions,
  triggerOnAction: boolean = false
) => {
  const [userLocation, setUserLocation] = useState<GeoLocation | undefined>()
  const [locationError, setLocationError] = useState<string | null>(null)

  const fetchLocation = useCallback(() => {
    if (!navigator.geolocation) {
      const error = 'Geolocation is not supported by this browser'
      setLocationError(error)
      return Promise.reject(new Error(error))
    }

    return new Promise<GeoLocation>((resolve, reject) => {
      // Geolocation is required to determine user's position for store locator functionality.
      // Location is not persisted nor sent automatically to backend services.
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
          setUserLocation(location)
          setLocationError(null)
          resolve(location)
        },
        (error) => {
          const errorMsg = `Geolocation error (code ${error.code}): ${error.message}`
          setLocationError(errorMsg)
          reject(new Error(errorMsg))
        },
        positionOptions ?? {}
      )
    })
  }, [positionOptions])

  useEffect(() => {
    if (!triggerOnAction) {
      fetchLocation().catch(() => {})
    }
  }, [triggerOnAction, fetchLocation])

  return { userLocation, locationError, fetchLocation }
}
